// KidRequestsInbox — parent sheet for everything kids have asked
// for. Surfaces pending requests first (with the canonical
// ingredients the kid combined), per-request actions: GENERATE
// RECIPE (runs generate-recipe with the ingredient list and
// saves a draft into user_recipes), MARK DONE, DISMISS.
//
// EMOJI-IN-UI EXCEPTION: src/experiments/kids-mode/** is the
// explicit opt-in.

import { useState } from "react";
import { COLOR, FONT, RADIUS } from "../../lib/tokens";
import { useKidRequests } from "../../lib/useKidRequests";
import { useKidProfiles } from "../../lib/useKidProfiles";

const FILTERS = [
  { id: "pending", label: "Pending"   },
  { id: "done",    label: "Done"      },
  { id: "all",     label: "All"       },
];

export function KidRequestsInbox({ userId, onClose, onOpenRecipeBook, onCookRequest }) {
  const { pending, done, rows, accept, dismiss, generateRecipe } = useKidRequests(userId);
  const { kids } = useKidProfiles(userId);
  const [filter, setFilter] = useState("pending");
  const [busyId, setBusyId] = useState(null);
  const [toast, setToast] = useState(null);

  const visible = filter === "pending" ? pending : filter === "done" ? done : rows;

  // Resolve kid name from the kid roster
  const kidName = (uid) => {
    if (!uid) return "A kid";
    const k = kids.find((kk) => kk.id === uid);
    return k?.name || "A kid";
  };

  const doGenerate = async (req) => {
    setBusyId(req.id);
    try {
      const out = await generateRecipe(req);
      setToast({ kind: "ok", text: `✓ "${out.recipe.title}" saved to your Recipe Book` });
    } catch (e) {
      console.warn("[kid-inbox] generate failed:", e?.message || e);
      setToast({ kind: "err", text: e?.message || "could not generate recipe" });
    } finally {
      setBusyId(null);
      setTimeout(() => setToast(null), 3500);
    }
  };

  // "Yes, I'll make it!" — the core of the loop. Flips the request to
  // 'seen' so the kid immediately sees ✋ "Coming!" on her Foods tab.
  const doAccept = async (req) => {
    setBusyId(req.id);
    try {
      await accept(req.id);
      setToast({ kind: "ok", text: `✓ ${kidName(req.kid_user_id)} can see it's coming` });
    } catch {
      setToast({ kind: "err", text: "couldn't update — try again" });
    }
    setBusyId(null);
    setTimeout(() => setToast(null), 2500);
  };
  // "Cook now" — opens Cook Mode for the request's recipe. Starting
  // the cook links the request to the session (DB trigger 0261) and
  // her strip flips to 🔥 "Cooking now". App resolves the recipe +
  // closes this sheet.
  const doCook = async (req) => {
    if (!onCookRequest) return;
    setBusyId(req.id);
    try { await onCookRequest(req); } catch (e) {
      console.warn("[kid-inbox] cook failed:", e?.message || e);
      setToast({ kind: "err", text: e?.message || "couldn't start the cook" });
      setTimeout(() => setToast(null), 3000);
    }
    setBusyId(null);
  };
  const doDismiss = async (req) => {
    setBusyId(req.id);
    try { await dismiss(req.id); } catch {}
    setBusyId(null);
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(30,30,30,0.5)",
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        zIndex: 220,
        padding: 0,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 560,
          height: "92vh",
          background: "#FAF6EE",
          borderRadius: "20px 20px 0 0",
          padding: "20px 0 calc(16px + env(safe-area-inset-bottom))",
          boxShadow: "0 -8px 28px rgba(0,0,0,0.22)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div style={{ padding: "0 20px 12px", flexShrink: 0 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              marginBottom: 6,
            }}
          >
            <div
              style={{
                fontFamily: FONT.itemTitle,
                fontStyle: "italic",
                fontSize: 22,
                color: COLOR.ink,
                flex: 1,
              }}
            >
              Kid Requests
            </div>
            {pending.length > 0 && (
              <div
                style={{
                  padding: "4px 10px",
                  background: COLOR.leaf,
                  color: "#fff",
                  borderRadius: 999,
                  fontFamily: FONT.mono,
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                }}
              >
                {pending.length} PENDING
              </div>
            )}
          </div>
          <div
            style={{
              fontFamily: FONT.forma,
              fontSize: 12,
              color: COLOR.muted,
              marginBottom: 12,
              lineHeight: 1.45,
            }}
          >
            Things the kids invented or asked for. Tap GENERATE RECIPE
            to turn the ingredients into a real recipe in your book.
          </div>

          <div style={{ display: "flex", gap: 6 }}>
            {FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setFilter(f.id)}
                style={{
                  padding: "8px 14px",
                  background: filter === f.id ? COLOR.leaf : "transparent",
                  color: filter === f.id ? "#fff" : COLOR.ink,
                  border: `1px solid ${filter === f.id ? COLOR.leaf : COLOR.border}`,
                  borderRadius: 999,
                  fontFamily: FONT.mono,
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: "0.08em",
                  cursor: "pointer",
                }}
              >
                {f.label.toUpperCase()}
              </button>
            ))}
          </div>

          {toast && (
            <div
              style={{
                marginTop: 10,
                padding: "10px 12px",
                background: toast.kind === "ok" ? COLOR.leafDeep : COLOR.roseDeep,
                border: `1px solid ${toast.kind === "ok" ? COLOR.leafDim : COLOR.roseDim}`,
                borderRadius: 8,
                color: toast.kind === "ok" ? COLOR.leaf : COLOR.rose,
                fontFamily: FONT.forma,
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              {toast.text}
            </div>
          )}
        </div>

        <div
          style={{
            flex: 1,
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
            padding: "4px 16px 16px",
          }}
        >
          {visible.length === 0 ? (
            <div
              style={{
                padding: "40px 20px",
                textAlign: "center",
                fontFamily: FONT.forma,
                fontSize: 14,
                color: COLOR.muted,
              }}
            >
              <div style={{ fontSize: 60, lineHeight: 1, marginBottom: 12, opacity: 0.5 }}>📭</div>
              {filter === "pending" ? "No pending requests" : filter === "done" ? "Nothing here yet" : "No requests"}
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {visible.map((req) => (
                <RequestCard
                  key={req.id}
                  request={req}
                  kidName={kidName(req.kid_user_id)}
                  busy={busyId === req.id}
                  canCook={!!(req.recipe_slug || req.generated_recipe_id) && !!onCookRequest}
                  onAccept={() => doAccept(req)}
                  onCook={() => doCook(req)}
                  onGenerate={() => doGenerate(req)}
                  onDismiss={() => doDismiss(req)}
                />
              ))}
            </div>
          )}
        </div>

        <div style={{ padding: "12px 20px 0", flexShrink: 0 }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              width: "100%",
              padding: 14,
              background: COLOR.leaf,
              color: "#fff",
              border: "none",
              borderRadius: 10,
              fontFamily: FONT.mono,
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: "0.08em",
              cursor: "pointer",
            }}
          >
            DONE
          </button>
        </div>
      </div>
    </div>
  );
}

function RequestCard({ request, kidName, busy, canCook, onAccept, onCook, onGenerate, onDismiss }) {
  const ings = Array.isArray(request.ingredients) ? request.ingredients : [];
  // request_kind === "recipe" AND ingredients > 0  → kid combined
  //   ingredients in Mix and wants a new recipe generated.
  // request_kind === "recipe" AND ingredients == 0 → kid tapped a
  //   parent-tagged recipe from Foods → Recipes. No generation needed;
  //   the parent already has the dish.
  // request_kind === "dessert"                     → same as above
  //   but tagged dessert; status=done DOESN'T re-open the dessert
  //   gate (handled in useDessertGate via .neq("request_kind", "dessert")).
  const isRecipe = request.request_kind === "recipe" || request.request_kind === "dessert";
  const isFamilyRecipeAsk = isRecipe && ings.length === 0;
  const isDessertAsk = request.request_kind === "dessert";
  // request_kind === 'pantry' → kid tapped a parent-tagged
  // pantry_items row from Foods. pantry_item_id pointer (migration
  // 0244) resolves back to the specific kitchen row. We render this
  // with a distinct "FROM KITCHEN" pill so the parent can tell at a
  // glance vs a craft-combine ask.
  const isPantryAsk = request.request_kind === "pantry";
  const hasImage = !!request.result_image_url;

  // Suspicion heuristic — flag combos where the result name implies
  // ingredients not in the inputs. Helps the parent spot AI
  // hallucinations before tapping GENERATE RECIPE.
  const suspicious = (() => {
    if (!isRecipe || ings.length === 0) return null;
    const name = (request.food_name || "").toLowerCase();
    const allWords = new Set(
      ings.flatMap((i) => (i.name || "").toLowerCase().split(/\s+/))
    );
    // These tokens commonly imply hidden ingredients
    const flagWords = {
      bread:   "needs flour, yeast, water",
      cornbread: "needs flour + milk + egg",
      cake:    "needs flour + egg + sugar + butter + milk",
      cookie:  "needs flour + butter + sugar",
      pancake: "needs flour + milk + egg",
      pudding: "needs milk + egg + sugar",
      garlic:  "needs garlic",
      vanilla: "needs vanilla",
      pizza:   "needs dough",
      pie:     "needs flour + butter (pie crust)",
      donut:   "needs flour + sugar + yeast",
      cheese:  "needs cheese (not just milk)",
    };
    for (const [token, why] of Object.entries(flagWords)) {
      if (!name.includes(token)) continue;
      // Skip if any ingredient name already contains this token
      let inIngredients = false;
      for (const w of allWords) {
        if (w.includes(token) || token.includes(w)) { inIngredients = true; break; }
      }
      if (!inIngredients) return why;
    }
    return null;
  })();

  return (
    <div
      style={{
        padding: 14,
        background: "#fff",
        border: `1px solid ${COLOR.border}`,
        borderRadius: 14,
        opacity: request.status === "pending" ? 1 : 0.7,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
        {hasImage ? (
          <div
            style={{
              width: 56,
              height: 56,
              background: `url(${request.result_image_url}) center / cover no-repeat`,
              borderRadius: 12,
              flexShrink: 0,
              border: `1px solid ${COLOR.border}`,
            }}
          />
        ) : (
          <div
            style={{
              width: 56,
              height: 56,
              fontSize: 38,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: COLOR.deep,
              borderRadius: 12,
              flexShrink: 0,
            }}
          >
            {request.food_emoji || (isRecipe ? "🍽️" : "🥗")}
          </div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontFamily: FONT.itemTitle,
              fontStyle: "italic",
              fontSize: 18,
              color: COLOR.ink,
              lineHeight: 1.2,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {request.food_name}
          </div>
          <div
            style={{
              fontFamily: FONT.mono,
              fontSize: 10,
              color: COLOR.sub,
              letterSpacing: "0.06em",
              marginTop: 2,
              display: "flex",
              alignItems: "center",
              gap: 6,
              flexWrap: "wrap",
            }}
          >
            <span>{kidName.toUpperCase()} · {relativeTime(request.created_at)}</span>
            {isFamilyRecipeAsk && (
              <span
                style={{
                  padding: "2px 7px",
                  background: isDessertAsk ? "#FFE0B0" : COLOR.leafDeep,
                  color: isDessertAsk ? "#7A5A0F" : COLOR.leaf,
                  borderRadius: 999,
                  fontFamily: FONT.mono,
                  fontSize: 9,
                  fontWeight: 700,
                  letterSpacing: "0.1em",
                }}
              >
                {isDessertAsk ? "TREAT" : "FAMILY RECIPE"}
              </span>
            )}
            {isPantryAsk && (
              <span
                style={{
                  padding: "2px 7px",
                  background: "#F2E9FA",
                  color: "#7A4E2D",
                  borderRadius: 999,
                  fontFamily: FONT.mono,
                  fontSize: 9,
                  fontWeight: 700,
                  letterSpacing: "0.1em",
                }}
              >
                FROM KITCHEN
              </span>
            )}
          </div>
        </div>
        {request.status !== "pending" && (
          <div
            style={{
              padding: "4px 10px",
              background: request.status === "done" ? COLOR.leafDeep : COLOR.roseDeep,
              color: request.status === "done" ? COLOR.leaf : COLOR.rose,
              borderRadius: 999,
              fontFamily: FONT.mono,
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: "0.08em",
            }}
          >
            {request.status.toUpperCase()}
          </div>
        )}
      </div>

      {suspicious && (
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 6,
            padding: "8px 10px",
            background: COLOR.roseDeep,
            border: `1px solid ${COLOR.roseDim}`,
            borderRadius: 8,
            marginBottom: 10,
            fontFamily: FONT.forma,
            fontSize: 12,
            color: COLOR.rose,
            fontWeight: 600,
            lineHeight: 1.4,
          }}
        >
          <span style={{ fontSize: 16, lineHeight: 1 }}>⚠️</span>
          <span>
            "{request.food_name}" {suspicious}. The recipe will use only
            what's listed — kid's name may not match the actual dish.
          </span>
        </div>
      )}

      {ings.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <div
            style={{
              fontFamily: FONT.mono,
              fontSize: 9,
              letterSpacing: "0.12em",
              fontWeight: 700,
              color: COLOR.sub,
              marginBottom: 4,
            }}
          >
            INGREDIENTS
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
            {ings.map((i, idx) => (
              <span
                key={idx}
                style={{
                  padding: "4px 9px",
                  background: COLOR.soil,
                  border: `1px solid ${COLOR.border}`,
                  borderRadius: 999,
                  fontFamily: FONT.forma,
                  fontSize: 12,
                  fontWeight: 600,
                  color: COLOR.ink,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <span style={{ fontSize: 14 }}>{i.emoji}</span>
                {i.name}
              </span>
            ))}
            {request.technique_emoji && (
              <span
                style={{
                  padding: "4px 9px",
                  background: COLOR.goldDeep,
                  border: `1px solid ${COLOR.goldDim}`,
                  borderRadius: 999,
                  fontFamily: FONT.forma,
                  fontSize: 12,
                  fontWeight: 600,
                  color: COLOR.gold,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <span style={{ fontSize: 14 }}>{request.technique_emoji}</span>
                {request.technique_name}
              </span>
            )}
          </div>
        </div>
      )}

      {request.status === "pending" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {/* PRIMARY: "Yes, I'll make it" — flips the kid to ✋ Coming!
              This is the acknowledgement that was missing; tapping it
              is what tells her she's getting it. */}
          <button
            type="button"
            onClick={onAccept}
            disabled={busy}
            className="mcm-focusable"
            style={{
              width: "100%",
              padding: "13px 10px",
              background: busy ? COLOR.leafDim : COLOR.leaf,
              color: "#fff",
              border: "none",
              borderRadius: 10,
              fontFamily: FONT.mono,
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: "0.06em",
              cursor: busy ? "wait" : "pointer",
              boxShadow: busy ? "none" : `0 4px 10px ${COLOR.leafDim}`,
              touchAction: "manipulation",
            }}
          >
            {busy ? "…" : `✋ YES — TELL ${kidName.split(" ")[0].toUpperCase()} IT'S COMING`}
          </button>
          {/* SECONDARY: cook now (when a recipe exists) OR generate one
              (when it needs an AI recipe first), plus dismiss. */}
          <div style={{ display: "flex", gap: 8 }}>
            {canCook ? (
              <button
                type="button"
                onClick={onCook}
                disabled={busy}
                className="mcm-focusable"
                style={{
                  flex: 1, padding: "11px 10px",
                  background: "transparent",
                  color: "#A34711",
                  border: "1px solid rgba(200,90,31,0.5)",
                  borderRadius: 10,
                  fontFamily: FONT.mono, fontSize: 11, fontWeight: 700,
                  letterSpacing: "0.06em",
                  cursor: busy ? "wait" : "pointer",
                  touchAction: "manipulation",
                }}
              >
                🔥 COOK IT NOW
              </button>
            ) : ings.length > 0 ? (
              <button
                type="button"
                onClick={onGenerate}
                disabled={busy}
                className="mcm-focusable"
                style={{
                  flex: 1, padding: "11px 10px",
                  background: "transparent",
                  color: COLOR.leaf,
                  border: `1px solid ${COLOR.leafDim}`,
                  borderRadius: 10,
                  fontFamily: FONT.mono, fontSize: 11, fontWeight: 600,
                  letterSpacing: "0.06em",
                  cursor: busy ? "wait" : "pointer",
                  touchAction: "manipulation",
                }}
              >
                {busy ? "GENERATING…" : "✨ MAKE A RECIPE"}
              </button>
            ) : null}
            <button
              type="button"
              onClick={onDismiss}
              disabled={busy}
              aria-label="dismiss"
              className="mcm-focusable"
              style={{
                padding: "11px 14px",
                background: "transparent",
                color: COLOR.muted,
                border: `1px solid ${COLOR.border}`,
                borderRadius: 10,
                fontSize: 14,
                cursor: "pointer",
                touchAction: "manipulation",
              }}
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function relativeTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60)        return "just now";
  if (diff < 3600)      return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400)     return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}

export default KidRequestsInbox;
